import {
  createPublicClient,
  createWalletClient,
  custom,
  defineChain,
  http,
  type Address,
  type Chain,
  type EIP1193Provider,
  type PublicClient,
  type WalletClient,
} from "viem";
import { config, type NetworkDef } from "../config";

export function toChain(net: NetworkDef): Chain {
  return defineChain({
    id: net.chainId,
    name: net.name,
    nativeCurrency: { name: net.nativeSymbol, symbol: net.nativeSymbol, decimals: 18 },
    rpcUrls: { default: { http: [net.rpcUrl] } },
    blockExplorers: { default: { name: net.name, url: net.explorer } },
  });
}

export const l2Chain = toChain(config.l2);
export const l1Chain = toChain(config.l1);

/** Read-only client for the L2 (home chain). Reads always work against the configured RPC. */
export const l2Public: PublicClient = createPublicClient({ chain: l2Chain, transport: http(config.l2.rpcUrl) });
export const l1Public: PublicClient = createPublicClient({ chain: l1Chain, transport: http(config.l1.rpcUrl) });

export function getInjected(): EIP1193Provider | undefined {
  return (globalThis as unknown as { ethereum?: EIP1193Provider }).ethereum;
}

/** Wallet client bound to the injected provider and a specific chain (for the write in question). */
export function walletFor(chain: Chain, account: Address): WalletClient | undefined {
  const injected = getInjected();
  if (!injected) return undefined;
  return createWalletClient({ account, chain, transport: custom(injected) });
}

/** Ask the injected wallet to switch to `chainId`, adding the chain first if it is unknown. */
export async function ensureChain(net: NetworkDef): Promise<void> {
  const injected = getInjected();
  if (!injected) throw new Error("No injected wallet");
  const hexId = `0x${net.chainId.toString(16)}`;
  try {
    await injected.request({ method: "wallet_switchEthereumChain", params: [{ chainId: hexId }] });
  } catch (err: unknown) {
    // 4902 = chain not added to the wallet yet; add it, then the caller can retry.
    if (typeof err === "object" && err !== null && (err as { code?: number }).code === 4902) {
      await injected.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: hexId,
            chainName: net.name,
            nativeCurrency: { name: net.nativeSymbol, symbol: net.nativeSymbol, decimals: 18 },
            rpcUrls: [net.rpcUrl],
            blockExplorerUrls: [net.explorer],
          },
        ],
      });
    } else {
      throw err;
    }
  }
}
